import React, { useState, useEffect } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import '../styles/SuspendModal.css';

export default function ActionSubscriptionModal({
    show,
    onClose,
    actionType = "suspend", // "suspend", "cancel", "activate"
    actionTitle,
    actionDescription,
    confirmMessage = '',
    actionButtonLabel,
    onSubmit,
    showReasonField = true, // show/hide reason textarea
}) {
    const [reason, setReason] = useState("");
    const [showFinalConfirm, setShowFinalConfirm] = useState(false);
    const [error, setError] = useState(null);
    const [apiLoading, setApiLoading] = useState(false);
    const [apiSuccess, setApiSuccess] = useState("");
    const [apiError, setApiError] = useState("");

    useEffect(() => {
        if (show) {
            setReason("");
            setShowFinalConfirm(false);
            setError(null);
            setApiSuccess("");
            setApiError("");
        }
    }, [show]);

    const handlePrimaryClick = async () => {
        if (showReasonField && !reason.trim()) {
            setError("Please provide a reason.");
            return;
        }

        setError(null);

        // If activate → directly call onSubmit and show result in main modal
        if (actionType === "activate") {
            setApiLoading(true);
            const result = await onSubmit(null); // no reason needed
            setApiLoading(false);

            if (result.success) {
                setApiSuccess(result.message);
                setTimeout(() => onClose(), 3000);
            } else {
                setApiError(result.message);
            }

            return;
        }
        setShowFinalConfirm(true);
    }

    const handleFinalConfirm = async () => {
        setApiLoading(true);
        const result = await onSubmit(showReasonField ? reason : null);
        setApiLoading(false);

        if (result.success) {
            setApiSuccess(result.message);
            setTimeout(() => {
                setShowFinalConfirm(false);
                onClose();
            }, 3000);
        } else {
            setApiError(result.message);
        }
    };

    // === Colors based on actionType ===
    const actionColors = {
        activate: "#2e7d32", // green
        suspend: "#B45032",  // red/orange
        cancel: "#d9534f",   // dark red
    };

    const primaryColor = actionColors[actionType] || "#6c757d"; // fallback gray
    const headingColor = primaryColor;
    const finalConfirmColor = primaryColor;

    return (
        <>
            {/* ---------- MAIN MODAL ---------- */}
            <Modal show={show} onHide={onClose} centered backdrop="static" size="lg">
                <Modal.Header>
                    <Modal.Title>{actionTitle}</Modal.Title>
                </Modal.Header>

                <div className="divider"></div>

                <Modal.Body>
                    <h6 style={{ color: headingColor }}>{actionTitle} ?</h6>
                    <p>{actionDescription}</p>

                    {showReasonField && (
                        <>
                            <p><strong>Reason</strong></p>
                            <Form.Control
                                as="textarea"
                                rows={4}
                                placeholder={`Enter reason to ${actionType}...`}
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                            />
                            {error && <Alert variant="danger" className="mt-2">{error}</Alert>}
                        </>
                    )}

                    <div className="renew-action-buttons mt-4 mb-4">
                        <Button variant="outline-secondary" style={{ width: "fit-content" }} onClick={onClose}>
                            {actionType === "cancel" ? "Close" : "Cancel"}
                        </Button>
                        <Button
                            style={{ width: "fit-content", backgroundColor: primaryColor, border: "none" }}
                            onClick={handlePrimaryClick}
                        >
                            {actionButtonLabel}
                        </Button>
                    </div>
                    {
                        actionType === "activate" && (
                            <>
                                {apiLoading && <p>Processing...</p>}
                                {apiSuccess && <Alert variant="success">{apiSuccess}</Alert>}
                                {apiError && <Alert variant="danger">{apiError}</Alert>}
                            </>
                        )
                    }
                </Modal.Body>
            </Modal>

            {/* ---------- FINAL CONFIRMATION MODAL ---------- */}
            {actionType !== "activate" && (
                <Modal
                    show={showFinalConfirm}
                    onHide={() => setShowFinalConfirm(false)}
                    backdrop="static"
                    dialogClassName="light-confirm-modal"
                    backdropClassName="my-light-backdrop"
                >
                    <Modal.Header>
                        <Modal.Title>Confirm {actionType.charAt(0).toUpperCase() + actionType.slice(1)}</Modal.Title>
                    </Modal.Header>

                    <Modal.Body>
                        <h5>{confirmMessage}</h5>

                        <div className="mt-4 mb-4" style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}>
                            {!apiSuccess && (
                                <>
                                    <Button
                                        variant="outline-secondary"
                                        style={{ width: "fit-content" }}
                                        onClick={() => setShowFinalConfirm(false)}
                                        disabled={apiLoading}
                                    >
                                        No, Go Back
                                    </Button>
                                    <Button
                                        style={{ width: "fit-content", backgroundColor: finalConfirmColor, border: "none" }}
                                        onClick={handleFinalConfirm}
                                        disabled={apiLoading}
                                    >
                                        Yes, {actionType.charAt(0).toUpperCase() + actionType.slice(1)}
                                    </Button>
                                </>
                            )}
                        </div>

                        {apiLoading && <p>Processing...</p>}
                        {apiSuccess && <Alert variant="success">{apiSuccess}</Alert>}
                        {apiError && <Alert variant="danger">{apiError}</Alert>}
                    </Modal.Body>
                </Modal>
            )}
        </>
    );
}
